/**
 * Duracion en horas decimales.
 *
 * El modelo guarda `2.50` porque sumar y facturar horas es aritmetica decimal,
 * pero nadie dice "dos coma cinco horas": la ayuda debajo del control traduce a
 * `2 h 30 min` mientras se escribe, y la lista muestra directamente esa forma.
 * Es el mismo valor, dicho como lo dice quien carga el parte de horas.
 *
 * Se edita como decimal —no como `HH:MM`— porque el dato ES un decimal: forzar
 * un formato de reloj obligaria a redondear los `0.33 h` que salen de un
 * cronometro.
 */

import { controlClass } from '@/components/fields/controls';
import { ReadonlyOutput } from '@/components/fields/FieldDisplay';
import { formatHours } from '@/components/fields/format';
import { fraccionDe, useNumericBuffer } from '@/components/fields/numeric';
import type { FieldInputProps } from '@/components/fields/types';

export function HoursField({
  id,
  meta,
  value,
  onChange,
  readonly,
  required,
  invalid,
  describedBy,
}: FieldInputProps) {
  const buffer = useNumericBuffer(value, onChange, {
    entero: false,
    decimal: meta.type === 'decimal',
    minFraccion: 0,
    maxFraccion: fraccionDe(meta),
  });

  if (readonly) {
    return <ReadonlyOutput id={id} meta={meta} value={value} {...(describedBy === undefined ? {} : { describedBy })} />;
  }

  const legible = formatHours(value);
  const idPista = `${id}-horas`;
  const descrito = [describedBy, legible === '' ? null : idPista].filter((parte) => parte != null).join(' ');

  return (
    <>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        className={controlClass(invalid, 'text-right tabular-nums')}
        value={buffer.texto}
        required={required}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        {...(descrito === '' ? {} : { 'aria-describedby': descrito })}
        autoComplete="off"
        onFocus={buffer.alEntrar}
        onChange={(evento) => {
          buffer.alEscribir(evento.target.value);
        }}
        onBlur={buffer.alSalir}
      />
      {legible !== '' && (
        <p id={idPista} className="mt-1 text-right text-xs tabular-nums text-fg-faint">
          {legible}
        </p>
      )}
    </>
  );
}
